import './Bullet.css'

import * as d3 from 'd3'
import { useEffect, useRef } from 'react'
import { useMediaQuery } from 'react-responsive'

import bulletChart from './bullet'

interface BulletChartProps {
  data: {
    title: string
    subtitle: string
    ranges: number[]
    measures: number[]
    markers: number[]
    order: string
  }
  width?: number
  duration?: number
  isPrint?: boolean
}

const BulletChart: React.FC<BulletChartProps> = ({
  data,
  width = 700,
  duration = 1000,
  isPrint,
}) => {
  const svgRef = useRef<SVGSVGElement>(null)

  const isDesktop = isPrint ? true : (useMediaQuery({ query: '(min-width: 768px)' }) ?? false)
  const isTablet = isPrint ? false : (useMediaQuery({ query: '(min-width: 700px)' }) ?? false)
  const isMobileLarge = isPrint ? false : (useMediaQuery({ query: '(min-width: 500px)' }) ?? false)
  const isMobileSmall = isPrint ? false : (useMediaQuery({ query: '(min-width: 400px)' }) ?? false)
  const chartWidth = isDesktop
    ? width
    : isTablet
      ? 600
      : isMobileLarge
        ? 600
        : isMobileSmall
          ? 500
          : 450

  useEffect(() => {
    if (!svgRef.current) return
    d3.select(svgRef.current).selectAll('*').remove() // Clears old SVG elements

    const margin = { top: 5, right: 40, bottom: 20, left: isTablet ? 200 : 150 }
    const chart = bulletChart().width(chartWidth - margin.left - margin.right)

    const svg = d3.select(svgRef.current).attr('class', 'bullet').attr('width', chartWidth)

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

    g.datum(data).call(chart as any) // Bind data with type assertion

    // Title handling with text wrapping
    const titleG = g
      .append('g')
      .attr('class', 'title')
      .attr('text-anchor', 'end')
      .attr('transform', 'translate(-10, 30)')

    // Add text element
    const titleText = titleG.append('text').attr('class', 'title')

    // Function to wrap text
    const wrapText = (
      text: d3.Selection<SVGTextElement, unknown, null, undefined>,
      width: number
    ) => {
      const words = data.title.split(/[\s]+/).reverse()

      if (!isTablet) {
        width = 500
      }

      const lineHeight = 1.1 // ems
      const y = 0
      let lineNumber = 0
      let line: string[] = []
      let tspan = text.append('tspan').attr('x', 0).attr('y', y).attr('dy', 0)

      while (words.length > 0) {
        const word = words.pop()!

        // Add the word first
        line.push(word)
        tspan.text(line.join(' '))

        // Check if this word requires a forced break AFTER it
        if (
          word === 'operating' ||
          word === 'expense' ||
          tspan.node()!.getComputedTextLength() > width
        ) {
          if (tspan.node()!.getComputedTextLength() > width) {
            line.pop() // Remove the last word if it exceeds width
            tspan.text(line.join(' '))
            line = [word] // Start new line with the last word
          } else {
            line = [] // Empty line to force new tspan
          }

          tspan = text
            .append('tspan')
            .attr('x', 0)
            .attr('y', y)
            .attr('dy', `${++lineNumber * lineHeight}em`)
            .text(word)
        }
      }
    }

    // Apply text wrapping with smaller width
    wrapText(titleText, 150) // Reduced from 180px to 150px

    // Adjust vertical position
    const titleHeight = titleText.node()!.getBBox().height
    if (isTablet) {
      titleG.attr('transform', `translate(-10, ${titleHeight / 2 + 10})`) // Reduced from 15 to 10
    }
  }, [data, chartWidth, duration])

  return <svg ref={svgRef} />
}

export default BulletChart
